'use client'

import { useState, useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import posthog from 'posthog-js'
import { canShowCapped, recordCappedShow } from '@/lib/popupSession'
import { pricingPlans } from '@/lib/plans'

interface ResourceConfig {
  title: string
  description: string
  bulletPoints: string[]
  ctaText: string
}

interface ScrollOfferPopupProps {
  postId: string
  resource: ResourceConfig
  capKey?: string
}

const DISMISSED_KEY = 'aibc_resource_dismissed'
const PROMO_CODE = 'NEWSLETTER50'

const offerBullets = [
  'Full course library: Claude Code, Cursor, MCP, AI agents',
  'Live build workshop every Monday with 1,200+ builders',
  'Templates, boilerplates & the SaaS Launch Kit',
]

export default function ScrollOfferPopup({ postId, resource, capKey }: ScrollOfferPopupProps) {
  const [visible, setVisible] = useState(false)
  const [showResource, setShowResource] = useState(false)
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [ctaLoading, setCtaLoading] = useState(false)

  const dismiss = useCallback(() => {
    setVisible(false)
    try {
      const dismissed = JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '{}')
      dismissed[postId] = Date.now()
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed))
    } catch {}
  }, [postId])

  useEffect(() => {
    try {
      const dismissed = JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '{}')
      if (dismissed[postId]) return
    } catch {}

    if (!canShowCapped(capKey)) return

    const cleanup = () => {
      window.removeEventListener('scroll', onScroll)
      document.removeEventListener('mouseleave', onExitIntent)
    }

    const trigger = () => {
      if (!canShowCapped(capKey)) {
        cleanup()
        return
      }
      recordCappedShow(capKey)
      setVisible(true)
      cleanup()
    }

    const onScroll = () => {
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)
      if (scrollPercent >= 0.15) trigger()
    }

    // Exit-intent: cursor leaves the viewport through the top (tab/close/back).
    const onExitIntent = (e: MouseEvent) => {
      if (e.clientY <= 0) trigger()
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    document.addEventListener('mouseleave', onExitIntent)
    return cleanup
  }, [postId, capKey])

  const handleCtaClick = async () => {
    if (ctaLoading) return

    if (posthog?.__loaded) {
      posthog.capture('blog_offer_cta_clicked', { post_id: postId, promo_code: PROMO_CODE })
    }
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'InitiateCheckout', { content_name: `blog_offer_${postId}` })
    }

    setCtaLoading(true)
    try {
      const monthly = pricingPlans.find((p) => p.name === 'Monthly')
      const res = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: monthly?.priceId,
          promoCode: PROMO_CODE,
          metadata: {
            plan: 'Monthly',
            isFromSkool: false,
            isLegacyPricing: false,
            sourcePage: `blog_popup_${postId}`,
          },
        }),
      })
      const { url } = await res.json()
      if (url) {
        window.location.href = url
      } else {
        setCtaLoading(false)
      }
    } catch {
      setCtaLoading(false)
    }
  }

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setStatus('loading')
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: `offer_resource_${postId}` }),
      })
      if (res.ok) {
        setStatus('success')
        if (posthog?.__loaded) {
          posthog.capture('blog_offer_resource_signup', { post_id: postId })
        }
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', {
            content_name: `resource_download_${postId}`,
            content_category: 'free_resource',
          })
        }
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            send_to: 'AW-17984603839/20ewCKLx7pkcEL-N3f9C',
            value: 1.0,
            currency: 'AUD',
          })
        }
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  if (!visible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismiss} />
      <div className="relative w-full max-w-md bg-[#111] border border-gray-700 rounded-2xl p-6 sm:p-8 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {status === 'success' ? (
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 mb-4">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Check your inbox!</h3>
            <p className="text-gray-400 text-sm">We&apos;ve sent the resource to your email. The 50% off code is waiting whenever you&apos;re ready.</p>
            <button onClick={dismiss} className="mt-4 text-sm text-gray-500 hover:text-white transition-colors">
              Close
            </button>
          </div>
        ) : showResource ? (
          <>
            <div className="mb-1">
              <span className="inline-block text-xs font-semibold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full mb-3">
                FREE RESOURCE
              </span>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{resource.title}</h3>
            <p className="text-gray-400 text-sm mb-4">{resource.description}</p>
            <ul className="space-y-2 mb-5">
              {resource.bulletPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="text-blue-400 mt-0.5 shrink-0">&#10003;</span>
                  {point}
                </li>
              ))}
            </ul>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                className="w-full bg-white/5 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors text-sm"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-white text-black font-semibold py-3 rounded-lg text-sm hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {status === 'loading' ? 'Sending...' : resource.ctaText}
              </button>
              {status === 'error' && (
                <p className="text-red-400 text-xs text-center">Something went wrong. Try again.</p>
              )}
            </form>
            <button
              onClick={() => setShowResource(false)}
              className="w-full text-center text-xs text-gray-500 hover:text-white transition-colors mt-3"
            >
              ← Back to the 50% off offer
            </button>
          </>
        ) : (
          <>
            <div className="mb-1">
              <span className="inline-block text-xs font-semibold text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded-full mb-3">
                MEMBER OFFER
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">50% off your first month</h3>
            <p className="text-gray-400 text-sm mb-4">
              <span className="text-white font-semibold">$18.50</span> your first month, then $37/mo. 30-day money-back guarantee, cancel anytime.
            </p>
            <ul className="space-y-2 mb-5">
              {offerBullets.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <span className="text-orange-400 mt-0.5 shrink-0">&#10003;</span>
                  {point}
                </li>
              ))}
            </ul>
            <button
              onClick={handleCtaClick}
              disabled={ctaLoading}
              className="block w-full text-center bg-orange-500 text-white font-semibold py-3 rounded-lg text-sm hover:bg-orange-400 transition-colors disabled:opacity-60"
            >
              {ctaLoading ? 'Redirecting to checkout...' : 'Claim 50% off →'}
            </button>
            <button
              onClick={() => setShowResource(true)}
              className="w-full text-center text-xs text-gray-500 hover:text-white transition-colors mt-3"
            >
              Not ready? Get the free course instead →
            </button>
          </>
        )}
      </div>
    </div>
  )
}
